import React, { MouseEvent, ReactNode } from 'react'
import { SxProps, Theme } from '@mui/material'

import Stack from '@mui/material/Stack'
import TotalCount from 'components/common/TotalCount/TotalCount'
import styles from './FilterTab.module.sass'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** The additional classes to append */
  className?: string
  /** Whether the tab is active or not */
  isActive?: boolean
  /** Whether the tab is disabled or not; with `role="tab"` it is also `aria-disabled` and ignores clicks */
  isDisabled?: boolean
  /** The amount of orders displayed on the right side */
  totalCount?: string
  /** The tab label displayed beside the icon  */
  label?: string | ReactNode
  /** The tab badge displayed on the right side  */
  badge?: ReactNode
  /** The icon displayed on the left side */
  icon?: ReactNode
  /** Onclick action to be called */
  onClick?: (e: MouseEvent) => unknown
  /** The tab height  */
  height?: string
  /** The tab radius  */
  radius?: string,
  /** Custom tab sx styles props */
  sx?: SxProps<Theme>,
  /** Id of the tab, for its panel's `aria-labelledby` */
  id?: string
  /** `tab` inside a `role="tablist"` container: the tab renders as a `<button type="button">` */
  role?: 'tab'
  /** Selected state announced with `role="tab"`, usually `isActive` */
  ariaSelected?: boolean
  /** Id of the panel the tab controls */
  ariaControls?: string
  /** Roving tab index: 0 on the selected tab, -1 on the others */
  tabIndex?: number
  /** Arrow-key navigation, handled by the tab list */
  onKeyDown?: (e: React.KeyboardEvent) => void
  /** Accessible name of `totalCount`, e.g. "12 photos", read instead of the bare number */
  countLabel?: string
  /** Native title: a hint on hover, read as the tab's description, e.g. why a disabled tab waits */
  title?: string
}

/**
 * @component FilterTab to filter content on page
 * @example
 * <FilterTab
 *  onClick={e => console.log(e)}
 *  className="class"
 *  isActive={true}
 *  totalCount="23"
 *  label="Ongoing"
 *  icon=<Icon />
 * />
 */
export const FilterTab: React.FC<Props> = ({
  className = '',
  radius = '.8rem',
  height = '4.8rem',
  isActive = false,
  isDisabled = false,
  totalCount = '',
  sx,
  badge,
  icon,
  label,
  onClick,
  id,
  role,
  ariaSelected,
  ariaControls,
  tabIndex,
  onKeyDown,
  countLabel,
  title,
}) => {
  if (isActive) className += ` ${styles.isActive}`
  if (isDisabled) className += ` ${styles.isDisabled}`
  const isTab = role === 'tab'
  // A button may only hold phrasing content
  const Wrap = isTab ? 'span' : 'div'

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      padding=".8rem 1.2rem"
      alignItems="center"
      gap=".6rem"
      sx={sx}
      borderRadius={radius}
      height={height}
      className={`${styles.filterTab} ${className}`.replace(/\s+/igm, ' ').trim()}
      onClick={e => onClick && !(isTab && isDisabled) && onClick(e)}
      id={id}
      role={role}
      aria-selected={ariaSelected}
      aria-controls={ariaControls}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      title={title}
      {...(isTab && { component: 'button', type: 'button', 'aria-disabled': isDisabled || undefined })}
    >
      <Wrap className={styles.iconLabelWrap}>
        {icon &&
          <Wrap className={styles.icon}>{icon}</Wrap>
        }
        {label &&
          <Wrap>{label}</Wrap>
        }
      </Wrap>
      {!!totalCount && !countLabel && <TotalCount totalCount={totalCount} isActive={isActive} isInline={isTab} />}
      {!!totalCount && !!countLabel &&
        <>
          <span aria-hidden="true" className={styles.count}><TotalCount totalCount={totalCount} isActive={isActive} isInline={isTab} /></span>
          <span className={styles.visuallyHidden}>{countLabel}</span>
        </>
      }
      {!!badge && badge}
    </Stack>
  )
}

export default FilterTab
